import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useRowSelection, type EnrollmentRow } from "./components/useEnrollmentSelection";
import { headerCellStyle, bodyCellStyle } from "./components/SelectableEnrollmentTable";

interface BulkAssignMentorModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  /** Candidate mentors — users holding the Mentor role (lookups.mentors) */
  mentors: Array<{ id: number; name: string }>;
  onSuccess: () => void;
}

// Row shape with the enrollment's existing mentor (from the search response).
interface MentorRow extends EnrollmentRow {
  mentorUserId?: number | null;
  mentorName?: string | null;
}

interface ResultRow {
  enrollmentId: number;
  status: "assigned" | "skipped" | "not_found" | "error";
  reason?: string;
  error?: string;
}

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    assigned:  { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:   { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found: { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    error:     { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const BulkAssignMentorModal: React.FC<BulkAssignMentorModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  mentors,
  onSuccess,
}) => {
  // Per-enrollment chosen "New Mentor" (blank = "Select Mentor", matching legacy).
  const [rowMentor, setRowMentor] = useState<Record<number, number | "">>({});
  const [assignAll, setAssignAll] = useState<number | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  const enrollments: MentorRow[] = useMemo(() => {
    const nameByEnquiryId = new Map<number, string>();
    for (const s of studentList) nameByEnquiryId.set(s.enquiryId, s.name);

    const out: MentorRow[] = [];
    for (const eqId of selectedEnquiryIds) {
      const list = enquiryEnrollmentsMap[eqId] || [];
      for (const enr of list) {
        out.push({
          enrollmentId: enr.enrollmentId,
          enquiryId: eqId,
          studentCode: enr.studentCode,
          statusName: enr.statusName,
          batchName: enr.batchName,
          studentName: nameByEnquiryId.get(eqId) || "",
          mentorUserId: enr.mentorUserId ?? null,
          mentorName: enr.mentorName ?? null,
        });
      }
    }
    return out;
  }, [selectedEnquiryIds, enquiryEnrollmentsMap, studentList]);

  const { selectedIds, selectedEnrollmentIds, toggle, toggleAll, allSelected } =
    useRowSelection(enrollments, show);

  // Reset the per-row mentor choices whenever the modal (re)opens.
  useEffect(() => {
    if (!show) return;
    setRowMentor({});
    setAssignAll("");
  }, [show]);

  const setRow = (enrollmentId: number, v: number | "") =>
    setRowMentor((prev) => ({ ...prev, [enrollmentId]: v }));

  // "Assign to all": fill every TICKED row's New Mentor with the chosen mentor.
  const onAssignAll = (v: number | "") => {
    setAssignAll(v);
    if (!v) return;
    setRowMentor((prev) => {
      const next = { ...prev };
      for (const e of enrollments) {
        if (selectedIds.has(e.enrollmentId)) next[e.enrollmentId] = v;
      }
      return next;
    });
  };

  const pendingAssignments = useMemo(
    () =>
      enrollments
        .filter((e) => selectedIds.has(e.enrollmentId) && rowMentor[e.enrollmentId])
        .map((e) => ({ enrollmentId: e.enrollmentId, userId: Number(rowMentor[e.enrollmentId]) })),
    [enrollments, selectedIds, rowMentor],
  );

  const handleClose = () => {
    setRowMentor({});
    setAssignAll("");
    setResults(null);
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (selectedEnrollmentIds.length === 0) {
      setError("Select at least one enrollment.");
      return;
    }
    if (pendingAssignments.length === 0) {
      setError("Pick a mentor for at least one enrollment.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkAssignMentor({ assignments: pendingAssignments });
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Bulk assign mentor failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results ? "Assign Mentor — Results" : "Assign Mentor (Bulk)"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            <Form.Group className="mb-3" style={{ maxWidth: 360 }}>
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Assign to all</Form.Label>
              <Form.Select
                size="sm"
                value={assignAll}
                onChange={(e) => onAssignAll(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">Select Mentor</option>
                {mentors.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Form.Select>
              <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>
                Fills the New Mentor for every ticked enrollment; you can still change rows individually.
              </div>
            </Form.Group>

            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-strong)", marginBottom: 6 }}>
              Enrollments ({selectedEnrollmentIds.length} of {enrollments.length} selected)
            </div>

            {enrollments.length === 0 ? (
              <div
                style={{
                  padding: 16,
                  background: "#fefce8",
                  color: "#854d0e",
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                The selected students have no enrollments.
              </div>
            ) : (
              <div
                style={{
                  maxHeight: 300,
                  overflow: "auto",
                  border: "1px solid var(--border-default)",
                  borderRadius: 6,
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...headerCellStyle, width: 36, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          disabled={submitting}
                          style={{ margin: 0, cursor: "pointer" }}
                        />
                      </th>
                      <th style={headerCellStyle}>Enrollment</th>
                      <th style={headerCellStyle}>Student</th>
                      <th style={headerCellStyle}>Existing Mentor</th>
                      <th style={headerCellStyle}>New Mentor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.map((e) => (
                      <tr key={e.enrollmentId}>
                        <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(e.enrollmentId)}
                            onChange={() => toggle(e.enrollmentId)}
                            disabled={submitting}
                            style={{ margin: 0, cursor: "pointer" }}
                          />
                        </td>
                        <td style={bodyCellStyle}>
                          <span style={{ fontWeight: 600 }}>#{e.enrollmentId}</span>
                          {e.studentCode && (
                            <span style={{ marginLeft: 6, color: "var(--text-muted)" }}>
                              {e.studentCode}
                            </span>
                          )}
                        </td>
                        <td style={bodyCellStyle}>
                          <span style={{ color: "var(--text-strong)" }}>{e.studentName}</span>
                          <span style={{ marginLeft: 6, color: "var(--text-faint)", fontSize: 11 }}>
                            Enq {e.enquiryId}
                          </span>
                        </td>
                        <td style={bodyCellStyle}>
                          <span style={{ color: "var(--text-body)" }}>{e.mentorName || "—"}</span>
                        </td>
                        <td style={bodyCellStyle}>
                          <Form.Select
                            size="sm"
                            value={rowMentor[e.enrollmentId] ?? ""}
                            onChange={(ev) =>
                              setRow(e.enrollmentId, ev.target.value ? Number(ev.target.value) : "")
                            }
                            disabled={submitting || !selectedIds.has(e.enrollmentId)}
                            style={{ minWidth: 160 }}
                          >
                            <option value="">Select Mentor</option>
                            {mentors.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.assigned ?? 0}</strong> assigned
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "assigned" && (
                          <span style={{ color: "var(--text-body)" }}>Mentor assigned</span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "already_assigned"
                              ? "Already has this mentor"
                              : r.reason || ""}
                          </span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || pendingAssignments.length === 0}
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Assigning…
                </>
              ) : (
                <>Assign Mentor ({pendingAssignments.length})</>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkAssignMentorModal;
